import {
  ChangeEventHandler,
  FocusEventHandler,
  forwardRef,
  HTMLInputTypeAttribute,
  useRef,
} from 'react';
import RawInput, { InputIcon, RawInputProps as TRawProps } from './RawInput';

/**
 * The known `HTMLInputTypeAttribute` literals with the open-ended `string`
 * fallback stripped out. Narrowing to the closed set is what lets us forbid a
 * specific value below: `Exclude<HTMLInputTypeAttribute, 'number'>` alone does
 * not, because `'number'` still matches the `string & {}` branch of
 * `HTMLInputTypeAttribute` and so stays assignable.
 */
type KnownInputType<T> = T extends string
  ? string extends T
    ? never
    : T
  : never;

export type InputProps = Omit<TRawProps, 'onChange' | 'onTouch' | 'type'> & {
  onChange?: (value: string) => void;
  onTouch?: () => void;
  changeTimeoutMs?: number;
  onChangeTimeout?: (value: string) => void;
  /**
   * The native input `type`. `'number'` is intentionally disallowed — use the
   * `NumberInput` component instead. It emits a typed `number | null` and
   * formats its value, rather than a raw `string` from a native number input.
   */
  type?: Exclude<KnownInputType<HTMLInputTypeAttribute>, 'number'>;
};

/**
 * The standard single-line text input.
 *
 * ## When to use
 *
 * - Free text, search, and email values
 *
 * ## When to choose another component
 *
 * Pick the most specific input for the data type:
 *
 * - **Any numeric value** — use `NumberInput`. Do not use
 *   `<Input type="number" />` (see composition rules).
 * - **Numbers adjusted in steps (quantities)** — use `IncrementableNumberInput`.
 * - **Money** — use `CurrencyInput`.
 * - **Percentages** — use `PercentageField`.
 * - **Passwords** — use `PasswordInput`. It has a show/hide toggle.
 * - **Files** — use `FileInput`.
 * - **Colours** — use `ColourSwatchInput`.
 *
 * ## Composition rules
 *
 * Use `NumberInput` for numeric fields. Do not use `<Input type="number" />`.
 *
 * - `<Input type="number" />` renders a native number input. Its `onChange`
 *   still emits a raw `string`, so callers must parse it themselves (e.g.
 *   `parseInt`, `Number`).
 * - `NumberInput` emits a typed `number | null` and formats its value. It
 *   supports `min`, `max`, `step`, and `format` (an `Intl.NumberFormatOptions`).
 *
 * The `type` prop on `Input` rejects `'number'` at compile time to enforce this.
 * Existing `<Input type="number" />` usages are suppressed with
 * `@ts-expect-error` and should migrate to `NumberInput`.
 *
 * ❌ Do not do this:
 *
 * ```tsx
 * <Input type="number" value={value} onChange={(value) => onChange(parseInt(value))} />
 * ```
 *
 * ✅ Use `NumberInput`:
 *
 * ```tsx
 * <NumberInput value={value} onChange={(value) => onChange(value)} />
 * ```
 *
 * Wrap inputs in a `Field` to get a label, helper text, and error display. See
 * the Field docs.
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      onChange,
      onTouch,
      onChangeTimeout,
      changeTimeoutMs = 1000,
      onFocus,
      type = 'text',
      ...rest
    },
    ref
  ) => {
    const changeTimeout = useRef<number | null>(null);

    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      onChange?.(e.target.value);

      if (changeTimeout?.current) {
        clearTimeout(changeTimeout.current);
      }
      changeTimeout.current = (setTimeout as any)(() => {
        if (onChangeTimeout) {
          onChangeTimeout(e.target.value);
        }
      }, changeTimeoutMs);
    };

    const handleTouch: FocusEventHandler<HTMLInputElement> = (e) => {
      onTouch?.();
      // Normal on focus event
      onFocus?.(e);
    };

    return (
      <RawInput
        ref={ref}
        type={type}
        onChange={handleChange}
        onFocus={handleTouch}
        {...rest}
      />
    );
  }
);

export default Input;

export { InputIcon };
